import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Table,
  ProgressBar,
} from 'react-bootstrap';
import {
  FiBarChart2,
  FiClock,
  FiRefreshCw,
  FiEdit3,
  FiVolume2,
  FiUsers,
  FiCalendar,
  FiTrendingUp,
  FiFilter,
} from 'react-icons/fi';
import { auditLogsApi } from '../services/api';
import { AuditMetrics, AuditorMetrics } from '../types';
import { LoadingSpinner } from '../components/common';
import { useToast } from '../contexts/ToastContext';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';

// Preset date ranges
const DATE_PRESETS = [
  { label: 'Últimos 7 días', getValue: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
  { label: 'Últimos 30 días', getValue: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { label: 'Este mes', getValue: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
  { label: 'Mes anterior', getValue: () => {
    const lastMonth = subDays(startOfMonth(new Date()), 1);
    return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
  }},
];

const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (minutes < 60) {
    return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
};

const formatPercent = (rate: number): string => {
  return `${Math.round(rate * 100)}%`;
};

const Metrics: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState<AuditMetrics | null>(null);
  const [startDate, setStartDate] = useState<string>(
    format(subDays(new Date(), 30), 'yyyy-MM-dd')
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), 'yyyy-MM-dd')
  );
  const [selectedAuditor, setSelectedAuditor] = useState<string>('');
  const [activePreset, setActivePreset] = useState<number>(1); // 30 días por defecto

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const data = await auditLogsApi.getMetrics(
        startDate,
        endDate,
        selectedAuditor || undefined
      );
      setMetrics(data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Error', 'No se pudieron cargar las métricas');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedAuditor, toast]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const applyPreset = (index: number) => {
    const preset = DATE_PRESETS[index];
    const { start, end } = preset.getValue();
    setStartDate(format(start, 'yyyy-MM-dd'));
    setEndDate(format(end, 'yyyy-MM-dd'));
    setActivePreset(index);
  };

  const handleAuditorChange = (auditorId: string) => {
    setSelectedAuditor(auditorId);
  };

  if (loading && !metrics) {
    return <LoadingSpinner message="Cargando métricas..." />;
  }

  return (
    <div className="metrics-page">
      {/* Header */}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="mb-1">
              <FiBarChart2 className="me-2" />
              Métricas de Auditoría
            </h2>
            <p className="text-muted mb-0">
              Analiza el desempeño y tiempos de revisión
            </p>
          </div>
          <Button
            variant="outline-primary"
            onClick={fetchMetrics}
            disabled={loading}
          >
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FiRefreshCw />
            )}
            <span className="ms-2">Actualizar</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 filters-card">
        <Card.Body>
          <Row className="align-items-end g-3">
            {/* Date Presets */}
            <Col xs={12} md={6} lg={4}>
              <Form.Label className="d-flex align-items-center gap-2">
                <FiCalendar />
                Período
              </Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {DATE_PRESETS.map((preset, index) => (
                  <Button
                    key={index}
                    variant={activePreset === index ? 'primary' : 'outline-secondary'}
                    size="sm"
                    onClick={() => applyPreset(index)}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
            </Col>

            {/* Custom Date Range */}
            <Col xs={6} md={3} lg={2}>
              <Form.Label>Desde</Form.Label>
              <Form.Control
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset(-1);
                }}
              />
            </Col>
            <Col xs={6} md={3} lg={2}>
              <Form.Label>Hasta</Form.Label>
              <Form.Control
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset(-1);
                }}
              />
            </Col>

            {/* Auditor Filter */}
            <Col xs={12} md={6} lg={4}>
              <Form.Label className="d-flex align-items-center gap-2">
                <FiFilter />
                Filtrar por Auditor
              </Form.Label>
              <Form.Select
                value={selectedAuditor}
                onChange={(e) => handleAuditorChange(e.target.value)}
              >
                <option value="">Todos los auditores</option>
                {metrics?.by_auditor.map((auditor) => (
                  <option key={auditor.auditor_id} value={auditor.auditor_id}>
                    {auditor.auditor_name} ({auditor.count})
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Main Stats Cards */}
      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="stat-icon text-primary">
                <FiTrendingUp />
              </div>
              <div className="stat-value">
                {metrics?.total_interpretations || 0}
              </div>
              <div className="stat-label">Interpretaciones Revisadas</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="stat-icon text-info">
                <FiClock />
              </div>
              <div className="stat-value">
                {metrics ? formatDuration(metrics.avg_review_time_seconds) : '-'}
              </div>
              <div className="stat-label">Tiempo Promedio de Revisión</div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="stat-icon text-warning">
                <FiRefreshCw />
              </div>
              <div className="stat-value">
                {metrics ? formatPercent(metrics.regeneration_rate) : '-'}
              </div>
              <div className="stat-label">Tasa de Regeneración</div>
              <ProgressBar
                variant="warning"
                now={(metrics?.regeneration_rate || 0) * 100}
                className="mt-2"
                style={{ height: '4px' }}
              />
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={3}>
          <Card className="stat-card h-100">
            <Card.Body>
              <div className="stat-icon text-success">
                <FiEdit3 />
              </div>
              <div className="stat-value">
                {metrics ? formatPercent(metrics.edit_rate) : '-'}
              </div>
              <div className="stat-label">Tasa de Edición</div>
              <ProgressBar
                variant="success"
                now={(metrics?.edit_rate || 0) * 100}
                className="mt-2"
                style={{ height: '4px' }}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Secondary Stats */}
      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} lg={4}>
          <Card className="stat-card-secondary h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="stat-icon-sm text-info">
                <FiVolume2 />
              </div>
              <div>
                <div className="stat-value-sm">
                  {metrics ? formatPercent(metrics.tts_usage_rate) : '-'}
                </div>
                <div className="stat-label-sm">Uso de TTS</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={4}>
          <Card className="stat-card-secondary h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="stat-icon-sm text-primary">
                <FiUsers />
              </div>
              <div>
                <div className="stat-value-sm">
                  {metrics?.by_auditor.length || 0}
                </div>
                <div className="stat-label-sm">Auditores Activos</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} lg={4}>
          <Card className="stat-card-secondary h-100">
            <Card.Body className="d-flex align-items-center gap-3">
              <div className="stat-icon-sm text-secondary">
                <FiCalendar />
              </div>
              <div>
                <div className="stat-value-sm">
                  {metrics?.period.start && metrics?.period.end
                    ? `${format(new Date(metrics.period.start), 'dd MMM', { locale: es })} - ${format(new Date(metrics.period.end), 'dd MMM', { locale: es })}`
                    : '-'}
                </div>
                <div className="stat-label-sm">Período Analizado</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Auditors Table */}
      <Card className="auditors-table-card">
        <Card.Header className="d-flex align-items-center gap-2">
          <FiUsers />
          <span>Desempeño por Auditor</span>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : metrics?.by_auditor && metrics.by_auditor.length > 0 ? (
            <Table responsive hover className="mb-0 auditors-table">
              <thead>
                <tr>
                  <th>Auditor</th>
                  <th className="text-center">Revisiones</th>
                  <th className="text-center">Tiempo Prom.</th>
                  <th className="text-center">Regeneraciones</th>
                  <th className="text-center">Ediciones</th>
                </tr>
              </thead>
              <tbody>
                {metrics.by_auditor.map((auditor: AuditorMetrics) => (
                  <tr key={auditor.auditor_id}>
                    <td>
                      <div className="auditor-name">
                        <div className="auditor-avatar">
                          {auditor.auditor_name.charAt(0).toUpperCase()}
                        </div>
                        <span>{auditor.auditor_name}</span>
                      </div>
                    </td>
                    <td className="text-center">
                      <strong>{auditor.count}</strong>
                    </td>
                    <td className="text-center">
                      {formatDuration(auditor.avg_review_time_seconds)}
                    </td>
                    <td className="text-center">
                      <span className={`rate-badge ${auditor.regeneration_rate > 0.2 ? 'high' : auditor.regeneration_rate > 0.1 ? 'medium' : 'low'}`}>
                        {formatPercent(auditor.regeneration_rate)}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="rate-badge">
                        {formatPercent(auditor.edit_rate)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="text-center py-4 text-muted">
              No hay datos de auditores para el período seleccionado
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default Metrics;
